import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import DashboardMainContainer from "../../../components/common/Containers/DashboardMainContainer";
import FieldDropdown from "../../../components/common/InputField/FieldDropdown";
import {
  ACCOUNT_POPUP_MUTED_PANEL,
  AccountPopupUploadArea,
  AccountPopupUploadPlaceholder,
} from "../../../components/BusinessDashboard/accountPopupFields";
import { runTextPipeline, validateFile, getAcceptAttribute } from "../../../utils/contentSafety";
import Alerts from "../../../components/common/Alerts/Alerts";
import {
  AdminPageTitle,
  AdminSectionTitle,
  AdminSupportingText,
  AdminDetailFieldLabel,
  AdminHeaderAction,
  AdminFormSecondaryAction,
  AdminLinkAction,
  adminWizardDropdownClasses,
  adminWizardTextareaClasses,
} from "../../../sections/AdminDashboard/shared/adminUx";
import {
  CUSTOMER_WIZARD_TITLE_CLASS,
  CUSTOMER_FORM_ACTION_ROW_BETWEEN_CLASS,
  CUSTOMER_FORM_ACTION_ROW_END_CLASS,
} from "../../../sections/CustomerDashboard/shared/customerLayoutSpec";
import { POPUP_FORM_CONTENT_CLASS } from "../../../components/popup/popupLayoutSpec";

const TICKET_WIZARD_SHELL_CLASS = "lg:px-5 lg:pb-10 relative !bg-white w-full";
const TICKET_WIZARD_VIEWPORT_CLASS =
  "overflow-auto relative flex items-center justify-center w-full";
const TICKET_WIZARD_INNER_CLASS = "w-full";

/** Support Tickets only — Appeals are a separate module and are not created here. */
const CATEGORY_OPTIONS = [
  { name: 'General' },
  { name: 'Billing' },
  { name: 'Technical' },
];

const STEP_NAMES = ["Select Category", "Description", "Attachment"];

const CreateNewTicket = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const initialStep = 1;

  const [step, setStep] = useState(initialStep);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [form, setForm] = useState({
    category: location.state?.category ?? null,
    description: location.state?.prefillDescription ?? "",
    attachment: null,
  });

  const handleChange = (field, value) => {
    if (field === "attachment" && value && value instanceof File) {
      const isImage = (value.type || "").startsWith("image/");
      const result = validateFile(value, isImage ? "image" : "document");
      if (!result.valid) {
        Alerts.error(result.message);
        return;
      }
    }
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmitTicket = async (e) => {
    e?.preventDefault?.();
    if (!form.category || !form.description.trim()) return;
    const descResult = runTextPipeline(form.description.trim(), "support");
    if (!descResult.valid) {
      Alerts.error(descResult.message);
      return;
    }
    setSubmitting(true);
    try {
      await new Promise((r) => setTimeout(r, 800));
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  const canProceedStep1 = !!form.category;
  const hasValidStep2Content = form.description.trim();

  if (submitted) {
    return (
      <DashboardMainContainer className={TICKET_WIZARD_SHELL_CLASS}>
        <div
          className={TICKET_WIZARD_VIEWPORT_CLASS}
          style={{ minHeight: "calc(100vh - 120px)" }}
        >
          <div className={POPUP_FORM_CONTENT_CLASS}>
            <div className={`${TICKET_WIZARD_INNER_CLASS} flex flex-col items-center text-center`}>
              <i className="fi fi-rs-check-circle text-4xl text-freshgreen mb-4" aria-hidden />
              <AdminSectionTitle className="text-offblack text-center">
                Your ticket has been submitted
              </AdminSectionTitle>
              <AdminSupportingText className="mt-2 text-offblack text-center block">
                We&apos;ll get back to you as soon as possible. You can track it under My Tickets → Open.
              </AdminSupportingText>
              <div className="mt-6 flex flex-wrap justify-center gap-3">
                <AdminHeaderAction
                  onClick={() => navigate("/customer-dashboard/tickets/open")}
                >
                  View tickets
                </AdminHeaderAction>
                <AdminFormSecondaryAction
                  onClick={() => {
                    setSubmitted(false);
                    setStep(1);
                    setForm({
                      category: null,
                      description: "",
                      attachment: null,
                    });
                  }}
                >
                  Create Another
                </AdminFormSecondaryAction>
              </div>
            </div>
          </div>
        </div>
      </DashboardMainContainer>
    );
  }

  return (
    <DashboardMainContainer className={TICKET_WIZARD_SHELL_CLASS}>
      <div
        className={TICKET_WIZARD_VIEWPORT_CLASS}
        style={{ minHeight: "calc(100vh - 120px)" }}
      >
        <div className={POPUP_FORM_CONTENT_CLASS}>
          <div className={TICKET_WIZARD_INNER_CLASS}>
            <AdminPageTitle className={CUSTOMER_WIZARD_TITLE_CLASS}>
              {STEP_NAMES[step - 1]}
            </AdminPageTitle>

          {step === 1 && (
            <div className="w-full">
              <div>
                <label className="block mb-2">
                  <AdminDetailFieldLabel className="text-offblack">Category</AdminDetailFieldLabel>
                </label>
                <FieldDropdown
                  surface="muted"
                  variant="general"
                  value={form.category}
                  onChange={(e) => handleChange("category", e.value)}
                  options={CATEGORY_OPTIONS}
                  optionLabel="name"
                  placeholder="Select category"
                  className={adminWizardDropdownClasses(false, true)}
                  panelClassName={ACCOUNT_POPUP_MUTED_PANEL}
                />
              </div>
              <div className={CUSTOMER_FORM_ACTION_ROW_END_CLASS}>
                <AdminHeaderAction type="button" onClick={() => setStep(2)} disabled={!canProceedStep1}>
                  Next
                </AdminHeaderAction>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="w-full">
              <div>
                <div className="space-y-1">
                  <AdminDetailFieldLabel htmlFor="ticket-description">Description</AdminDetailFieldLabel>
                  <textarea
                    id="ticket-description"
                    placeholder="Include any error messages, steps to reproduce, or relevant account details..."
                    value={form.description}
                    onChange={(e) => handleChange("description", e.target.value)}
                    className={adminWizardTextareaClasses(false)}
                    maxLength={2000}
                    rows={5}
                    style={{ resize: "vertical" }}
                  />
                </div>
              </div>
              <div className={CUSTOMER_FORM_ACTION_ROW_BETWEEN_CLASS}>
                <AdminLinkAction type="button" onClick={() => setStep(1)}>
                  Back
                </AdminLinkAction>
                <AdminHeaderAction
                  type="button"
                  onClick={() => {
                    const desc = form.description?.trim() ?? "";
                    if (!desc) {
                      Alerts.error("Please enter a description.");
                      return;
                    }
                    const descResult = runTextPipeline(desc, "support");
                    if (!descResult.valid) {
                      Alerts.error(descResult.message);
                      return;
                    }
                    setStep(3);
                  }}
                  disabled={!hasValidStep2Content}
                >
                  Next
                </AdminHeaderAction>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="w-full">
              <div className="flex items-center justify-center w-full">
                <input
                  id="ticket-attachment-input"
                  type="file"
                  accept={`${getAcceptAttribute("image")},${getAcceptAttribute("document")}`}
                  className="cursor-pointer hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0] || null;
                    e.target.value = "";
                    if (!file) return;
                    const isImage = (file.type || "").startsWith("image/");
                    const result = validateFile(file, isImage ? "image" : "document");
                    if (!result.valid) {
                      Alerts.error(result.message);
                      return;
                    }
                    handleChange("attachment", file);
                  }}
                />
                <AccountPopupUploadArea htmlFor="ticket-attachment-input">
                  {form.attachment ? (
                    <div className="my-auto flex flex-col items-center">
                      <i className="fi fi-rs-file text-base text-primaryblue" aria-hidden />
                      <AdminSupportingText className="mt-1 text-primaryblue block">
                        {form.attachment.name}
                      </AdminSupportingText>
                      <AdminLinkAction
                        type="button"
                        className="mt-2"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleChange("attachment", null);
                        }}
                      >
                        Remove file
                      </AdminLinkAction>
                    </div>
                  ) : (
                    <AccountPopupUploadPlaceholder
                      label="Drag & drop or Click to upload"
                      hint="Images or documents. Max 10MB images, 20MB documents."
                    />
                  )}
                </AccountPopupUploadArea>
              </div>
              <div className={CUSTOMER_FORM_ACTION_ROW_BETWEEN_CLASS}>
                <AdminLinkAction type="button" onClick={() => setStep(2)}>
                  Back
                </AdminLinkAction>
                <AdminHeaderAction type="button" onClick={handleSubmitTicket} disabled={submitting}>
                  {submitting ? "Submitting..." : "Submit"}
                </AdminHeaderAction>
              </div>
            </div>
          )}
          </div>
        </div>
      </div>
    </DashboardMainContainer>
  );
};

export default CreateNewTicket;
